import { layoutColumns } from '@qronstat/shared';
import type { LayoutTile, ScreenLayout, ScreenLayoutV1, ScreenLayoutV2 } from '@qronstat/shared';
import type { KpiItem, WidgetDef, WidgetKind } from './types';

export type LayoutMode = 'wide' | 'phone';
export type TileKind = WidgetKind | 'kpi';

export const rowUnit = 8;
export const maxTileHeight = 4000;

export interface Unit {
  id: string;
  def: WidgetDef;
  item: KpiItem | null;
  kind: TileKind;
  defaultWide: number;
  defaultPhone: number;
}

export interface Placed {
  unit: Unit;
  w: number;
  h: number | null;
  hidden: boolean;
}

const minWidths: Partial<Record<TileKind, number>> = {
  kpi: 10,
  map: 30,
  objects: 30,
  table: 30,
  records: 30,
  tariffs: 30,
  conversion: 30,
  batteryWear: 30,
  thresholds: 30,
  cohort: 30,
  funnel: 30,
  heatmap: 30,
  reconciliation: 30,
  compare: 30,
  ranking: 15,
  distribution: 15,
  connection: 20,
};

const minHeights: Partial<Record<TileKind, number>> = {
  kpi: 160,
  map: 320,
  timeseries: 240,
  distribution: 220,
  heatmap: 240,
  connection: 240,
  objects: 200,
  table: 200,
  records: 200,
  tariffs: 200,
  conversion: 200,
  batteryWear: 200,
  thresholds: 200,
  cohort: 200,
  funnel: 200,
};

export function minWidth(kind: TileKind, mode: LayoutMode): number {
  if (mode === 'phone') return kind === 'kpi' ? 30 : layoutColumns;
  return minWidths[kind] ?? 20;
}

export function minHeight(kind: TileKind): number {
  return minHeights[kind] ?? 140;
}

export function kpiRowWidths(count: number): number[] {
  if (count <= 0) return [];
  const perRow = count <= 5 ? count : count === 6 ? 3 : count <= 8 ? 4 : 5;
  const widths: number[] = [];
  for (let start = 0; start < count; start += perRow) {
    const inRow = Math.min(perRow, count - start);
    const base = Math.floor(layoutColumns / inRow);
    const extra = layoutColumns - base * inRow;
    for (let i = 0; i < inRow; i++) widths.push(base + (i < extra ? 1 : 0));
  }
  return widths;
}

export function kpiTileId(widgetId: string, metric: string): string {
  return `${widgetId}:${metric}`;
}

export function screenUnits(widgets: readonly WidgetDef[]): Unit[] {
  return widgets.flatMap((def): Unit[] => {
    if (def.kind === 'kpis') {
      const widths = kpiRowWidths(def.items.length);
      return def.items.map((item, i) => ({
        id: kpiTileId(def.id, item.metric),
        def,
        item,
        kind: 'kpi',
        defaultWide: widths[i] ?? 12,
        defaultPhone: 30,
      }));
    }
    return [
      { id: def.id, def, item: null, kind: def.kind, defaultWide: def.span * 5, defaultPhone: layoutColumns },
    ];
  });
}

export function fromV1(layout: ScreenLayoutV1, units: readonly Unit[]): LayoutTile[] {
  return layout.items.flatMap((entry): LayoutTile[] => {
    const own = units.filter((u) => u.def.id === entry.id);
    if (!own.length) return [];
    if (own.length === 1 && own[0]?.kind !== 'kpi')
      return [{ id: entry.id, w: entry.span * 5, hidden: entry.hidden }];
    return own.map((u) => ({ id: u.id, w: u.defaultWide, hidden: entry.hidden }));
  });
}

function tilesOf(layout: ScreenLayout, units: readonly Unit[], mode: LayoutMode): LayoutTile[] | null {
  if ('version' in layout) return mode === 'phone' ? (layout.phone ?? null) : layout.wide;
  return mode === 'phone' ? null : fromV1(layout, units);
}

function clampWidth(w: number, kind: TileKind, mode: LayoutMode): number {
  const bounded = Math.max(minWidth(kind, mode), Math.min(layoutColumns, Math.round(w)));
  return mode === 'phone' ? (bounded > 30 ? layoutColumns : 30) : bounded;
}

function clampHeight(h: number | null | undefined, kind: TileKind): number | null {
  if (h === null || h === undefined) return null;
  return Math.max(minHeight(kind), Math.min(maxTileHeight, Math.round(h / rowUnit) * rowUnit));
}

export function defaultPlacement(units: readonly Unit[], mode: LayoutMode): Placed[] {
  return units.map((unit) => ({
    unit,
    w: mode === 'phone' ? unit.defaultPhone : unit.defaultWide,
    h: null,
    hidden: false,
  }));
}

export function applyLayout(
  units: readonly Unit[],
  layout: ScreenLayout | null | undefined,
  mode: LayoutMode,
): Placed[] {
  const base = defaultPlacement(units, mode);
  if (!layout) return base;
  let tiles = tilesOf(layout, units, mode);
  let derived = false;
  if (!tiles && mode === 'phone') {
    tiles = tilesOf(layout, units, 'wide');
    derived = true;
  }
  if (!tiles?.length) return base;
  const byId = new Map(base.map((p) => [p.unit.id, p]));
  const placed: Placed[] = [];
  const used = new Set<string>();
  for (const tile of tiles) {
    const found = byId.get(tile.id);
    if (!found || used.has(tile.id)) continue;
    used.add(tile.id);
    placed.push({
      unit: found.unit,
      w: derived ? found.w : clampWidth(tile.w, found.unit.kind, mode),
      h: derived ? null : clampHeight(tile.h, found.unit.kind),
      hidden: tile.hidden,
    });
  }
  base.forEach((p, index) => {
    if (used.has(p.unit.id)) return;
    const previous = base
      .slice(0, index)
      .reverse()
      .find((q) => used.has(q.unit.id));
    const at = previous ? placed.findIndex((q) => q.unit.id === previous.unit.id) + 1 : 0;
    placed.splice(at, 0, p);
    used.add(p.unit.id);
  });
  return placed;
}

export function toTiles(placed: readonly Placed[]): LayoutTile[] {
  return placed.map((p) => ({
    id: p.unit.id,
    w: p.w,
    ...(p.h !== null ? { h: p.h } : {}),
    hidden: p.hidden,
  }));
}

export function isDefaultPlacement(
  placed: readonly Placed[],
  units: readonly Unit[],
  mode: LayoutMode,
): boolean {
  const base = defaultPlacement(units, mode);
  return (
    placed.length === base.length &&
    placed.every((p, i) => {
      const d = base[i];
      return d !== undefined && p.unit.id === d.unit.id && p.w === d.w && p.h === null && !p.hidden;
    })
  );
}

export function mergeLayout(
  stored: ScreenLayout | null | undefined,
  units: readonly Unit[],
  mode: LayoutMode,
  placed: readonly Placed[],
): ScreenLayoutV2 | null {
  const own = isDefaultPlacement(placed, units, mode) ? null : toTiles(placed);
  const otherMode: LayoutMode = mode === 'phone' ? 'wide' : 'phone';
  const otherTiles = stored ? tilesOf(stored, units, otherMode) : null;
  const other = otherTiles?.length ? otherTiles : null;
  const wide = mode === 'wide' ? own : other;
  const phone = mode === 'phone' ? own : other;
  if (!wide && !phone) return null;
  return {
    version: 2,
    wide: wide ?? [],
    ...(phone ? { phone } : {}),
  };
}

export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item as T);
  return next;
}

export interface ResizeInput {
  startWidth: number;
  startHeight: number;
  dx: number;
  dy: number;
  columnWidth: number;
  kind: TileKind;
  mode: LayoutMode;
}

export function resizeTile(input: ResizeInput): { w: number; h: number } {
  const columns = (input.startWidth * input.columnWidth + input.dx) / Math.max(1, input.columnWidth);
  return {
    w: clampWidth(columns, input.kind, input.mode),
    h: clampHeight(input.startHeight + input.dy, input.kind) ?? minHeight(input.kind),
  };
}

export function displayWidth(w: number, viewport: 'phone' | 'tablet' | 'desktop'): number {
  if (viewport !== 'tablet') return w;
  if (w < 20) return 20;
  return w >= 40 ? layoutColumns : w;
}
